/**
 * Purpose: Decide whether PostHog may run in this process, and whether flags are forced on locally.
 * Inputs: Optional env overrides (NODE_ENV, NEXT_PUBLIC_SITE_URL, NEXT_PUBLIC_PLAYWRIGHT_E2E) and hostname.
 * Outputs: Booleans for capture enablement and the localhost all-flags-on override.
 */
import { logger } from '@lib/logger'

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]', '::1'])

export function isLocalAnalyticsHost(hostname: string): boolean {
  return LOCAL_HOSTS.has(hostname)
}

export type PostHogEnablementEnv = {
  NEXT_PUBLIC_PLAYWRIGHT_E2E?: string
  NEXT_PUBLIC_SITE_URL?: string
  NODE_ENV?: string
}

/**
 * Resolve env for the enablement check.
 *
 * Next.js only inlines `process.env.NEXT_PUBLIC_*` when each key is a static
 * member access. Passing `process.env` around as an object leaves those values
 * undefined in the client bundle — which would silently disable PostHog in
 * Playwright (hostname is localhost) even when NEXT_PUBLIC_PLAYWRIGHT_E2E is set.
 */
function resolveEnv(processEnv?: PostHogEnablementEnv): PostHogEnablementEnv {
  if (processEnv) return processEnv
  return {
    NEXT_PUBLIC_PLAYWRIGHT_E2E: process.env.NEXT_PUBLIC_PLAYWRIGHT_E2E,
    NEXT_PUBLIC_SITE_URL: process.env.NEXT_PUBLIC_SITE_URL,
    NODE_ENV: process.env.NODE_ENV,
  }
}

/**
 * Whether PostHog may send analytics, exceptions, feature-flag traffic, or
 * OTLP logs from this process.
 *
 * Off on localhost so local runs never pollute the project. Exceptions:
 * - Playwright e2e (`NEXT_PUBLIC_PLAYWRIGHT_E2E=true`) runs against
 *   http://localhost:3000 but asserts on `/ingest` traffic.
 * - Vitest (`NODE_ENV=test`) keeps the SDK path exercisable under jsdom,
 *   which reports hostname `localhost`.
 */
export function isPostHogCaptureEnabled(
  processEnv?: PostHogEnablementEnv,
  hostname?: string | null,
): boolean {
  const env = resolveEnv(processEnv)
  const host =
    hostname !== undefined
      ? hostname
      : typeof window !== 'undefined'
        ? window.location.hostname
        : null

  if (env.NEXT_PUBLIC_PLAYWRIGHT_E2E === 'true') return true
  if (env.NODE_ENV === 'test') return true

  if (host != null && isLocalAnalyticsHost(host)) return false

  if (env.NODE_ENV !== 'production') return false

  const siteUrl = env.NEXT_PUBLIC_SITE_URL
  if (siteUrl) {
    try {
      if (isLocalAnalyticsHost(new URL(siteUrl).hostname)) return false
    } catch (err) {
      // Invalid SITE_URL — do not treat as localhost.
      logger.warn('posthog-enabled', 'invalid NEXT_PUBLIC_SITE_URL', { error: err instanceof Error ? err.message : String(err) })
    }
  }

  return true
}

/**
 * Whether every PostHog feature flag should read as on, regardless of PostHog.
 *
 * True exactly where {@link isPostHogCaptureEnabled} is false — `next dev`, a
 * loopback browser hostname, or a local `next start` — because flags never load
 * there. Vitest and Playwright keep capture on, so they still exercise real
 * flag evaluation (and their mocks), and deployed environments are unaffected.
 */
export function areAllFlagsForcedOn(
  processEnv?: PostHogEnablementEnv,
  hostname?: string | null,
): boolean {
  return !isPostHogCaptureEnabled(processEnv, hostname)
}
